import type { CofQuizQuestionKind, CofSessionStats } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import styles from './cof-wheel-summary.module.css'

export interface CofWheelSummaryProps {
  stats: CofSessionStats
  onRestart: () => void
  onSwitchToExplore: () => void
}

/** Readable labels for the per-kind breakdown rows. */
const KIND_LABELS: Record<CofQuizQuestionKind, string> = {
  'find-relative': 'Find the relative',
  'find-closely-related': 'Find a closely-related key',
  'find-parallel': 'Find the parallel',
  'count-signature': 'Count the signature',
  'spell-enharmonic': 'Spell the enharmonic',
}

const KIND_ORDER: readonly CofQuizQuestionKind[] = [
  'find-relative',
  'find-closely-related',
  'find-parallel',
  'count-signature',
  'spell-enharmonic',
]

/**
 * CoF Wheel quiz summary — final score + per-kind breakdown + restart. Only
 * kinds that were actually asked this session (total > 0) are listed, so a
 * single-track sub-mode (Relationships / Signatures) shows just its kinds.
 *
 * Spec: docs/superpowers/specs/2026-05-28-cof-wheel-v1.5-design.md §6
 */
export const CofWheelSummary: FC<CofWheelSummaryProps> = props => {
  const { stats, onRestart, onSwitchToExplore } = props
  const askedKinds = KIND_ORDER.filter(kind => stats.byKind[kind].total > 0)
  return (
    <section className={styles.panel} aria-label="Quiz summary">
      <h2 className={styles.heading}>Quiz complete</h2>
      <p className={styles.score}>
        {stats.correct} / {stats.total}
      </p>
      <ul className={styles.breakdown}>
        {askedKinds.map(kind => (
          <li key={kind}>
            {KIND_LABELS[kind]}: {stats.byKind[kind].correct} / {stats.byKind[kind].total}
          </li>
        ))}
      </ul>
      <div className={styles.actions}>
        <Button variant="contained" onClick={onRestart}>
          Restart
        </Button>
        <Button variant="outlined" onClick={onSwitchToExplore}>
          Back to Explore
        </Button>
      </div>
    </section>
  )
}
